/**
 * Sprint 23.6 v2 — agent profile (replaces the flat 14-panel cockpit layout).
 *
 * Two shapes coexist on the same route:
 *
 *   1. Per-agent profile — hero (identity strip → Live → Decisions+Learnings
 *      two-column band → Retro → Scorecard) + sticky tab bar (→ URL hash →
 *      existing panels) + a 4-block Workforce sidebar. The hero sections are
 *      separated by hairline bottom-borders (flush, no flex gap) per the
 *      visual-locked spec.
 *
 *   2. Workspace-root / folder cockpit — keeps the Sprint ≤22 panel layout: the
 *      root is the workspace itself, folders are organisation; neither is an
 *      "employee" to profile. Deliberately preserved.
 */

import { useParams, useNavigate } from "react-router";
import { useLibraryNode } from "../../lib/hooks/use-kuvio";
import { useAuth } from "../../lib/hooks/use-auth";
import { Card, EmptyState, Breadcrumb, type BreadcrumbSegment } from "../components/primitives";
import { useChatRail } from "../components/chat/ChatRailContext";
import { useChatScope } from "../hooks/use-chat-scope";
import { CockpitHeader } from "../components/cockpit/CockpitHeader";
import { InheritanceStrip } from "../components/cockpit/InheritanceStrip";
import { InstructionEditor } from "../components/cockpit/InstructionEditor";
import { AgentsPanel } from "../components/cockpit/AgentsPanel";
import { MemoriesPanel } from "../components/cockpit/MemoriesPanel";
import { LearningsPanel } from "../components/cockpit/LearningsPanel";
import { PagesPanel } from "../components/cockpit/PagesPanel";
import { PromptsPanel } from "../components/cockpit/PromptsPanel";
import { OwnedTablesGrid } from "../components/cockpit/OwnedTablesGrid";
import { TasksPanel } from "../components/cockpit/TasksPanel";
import { RoutinesPanel } from "../components/cockpit/RoutinesPanel";
import { ActivityPanel } from "../components/cockpit/ActivityPanel";
import { ActiveWorkPanel } from "../components/cockpit/ActiveWorkPanel";
import { FilesPanel } from "../components/cockpit/FilesPanel";
import { RecentActivity } from "../components/cockpit/RecentActivity";

import { AgentProfileHeader } from "../components/agent-profile/AgentProfileHeader";
import { AgentActionRow } from "../components/agent-profile/AgentActionRow";
import { AgentLiveActivity } from "../components/agent-profile/AgentLiveActivity";
import { AgentDecisionsAndLearnings } from "../components/agent-profile/AgentDecisionsAndLearnings";
import { AgentRetroSnippet } from "../components/agent-profile/AgentRetroSnippet";
import { AgentScorecard } from "../components/agent-profile/AgentScorecard";
import { AgentProfileTabs } from "../components/agent-profile/AgentProfileTabs";
import { AgentProfileTabContent } from "../components/agent-profile/AgentProfileTabContent";
import { AgentProfileSidebar } from "../components/agent-profile/AgentProfileSidebar";
import { AgentProvenanceFooter } from "../components/agent-profile/AgentProvenanceFooter";
// №16 D-WF13 — AgentActiveWorkSection (legacy workflow active-work) retired.

export function CockpitPage() {
  const { nodeId } = useParams<{ nodeId: string }>();
  const node = useLibraryNode(nodeId);
  const navigate = useNavigate();
  const { expanded } = useChatRail();
  const { user, currentWorkspaceId } = useAuth();
  const isOwner =
    user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";

  // Sprint 22 — chat scope auto-pins to this agent. Workspace root + folder
  // cockpits → workspace scope (no persona to ask about).
  const data = node.data;
  const isRootScope = data?.is_root === true;
  const isFolderScope = data?.type === "folder";
  useChatScope(
    data
      ? isRootScope || isFolderScope
        ? { kind: "workspace", label: "This workspace" }
        : { kind: "agent", id: data.id, label: data.name }
      : { kind: "workspace", label: "This workspace" },
    data && !isRootScope && !isFolderScope
      ? { kind: "agent", id: data.id, name: data.name }
      : null,
    [data?.id, data?.is_root, data?.name, data?.type],
  );

  if (node.isLoading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div
          style={{
            background: "var(--color-background-secondary)",
            borderRadius: "var(--border-radius-lg)",
            height: 120,
          }}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div style={{ background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)", height: 80 }} />
          <div style={{ background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)", height: 80 }} />
        </div>
      </div>
    );
  }

  if (node.error || !node.data) {
    return (
      <Card>
        <EmptyState>{node.error?.message ?? "Agent not found"}</EmptyState>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <a href="/workforce" style={{ fontSize: 13, color: "var(--color-text-info)" }}>
            Back to workforce
          </a>
        </div>
      </Card>
    );
  }

  const detail = node.data;
  const isRoot = !!detail.is_root;

  // Workspace root + folder cockpits keep the Sprint ≤22 layout.
  if (isRoot || detail.type === "folder") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: expanded ? 960 : undefined }}>
        <CockpitHeader node={detail} />
        {detail.type !== "folder" && <InheritanceStrip node={detail} />}
        {detail.type !== "folder" && <InstructionEditor node={detail} />}
        {detail.type !== "folder" && <TasksPanel nodeId={detail.id} includeSubtree={isRoot} />}
        {detail.type !== "folder" && <RoutinesPanel nodeId={detail.id} />}
        {detail.type !== "folder" && <ActivityPanel nodeId={detail.id} />}
        <AgentsPanel nodeId={detail.id} isRoot={isRoot} hideWhenEmpty={detail.type === "folder"} />
        <PagesPanel nodeId={detail.id} workspaceWide={isRoot} />
        <PromptsPanel nodeId={detail.id} nodeName={detail.name} />
        {detail.type !== "folder" && <MemoriesPanel nodeId={detail.id} />}
        {detail.type !== "folder" && <LearningsPanel nodeId={detail.id} />}
        <OwnedTablesGrid nodeId={detail.id} />
        {detail.type !== "folder" && <RecentActivity nodeId={detail.id} />}
        <FilesPanel nodeId={detail.id} nodeName={detail.name} />
        {isRoot && <ActiveWorkPanel rootNodeId={detail.id} />}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Per-agent profile — the Sprint 23.6 v2 hero + tabs + sidebar.
  // -----------------------------------------------------------------------
  const canEdit = !!isOwner; // Sprint 14c — members read-only on persona / settings.
  const segments: BreadcrumbSegment[] = (detail._breadcrumb ?? []).map((b) => ({
    label: b.name,
    onClick: () => navigate(`/agents/${b.id}`),
  }));
  segments.push({ label: detail.name });

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: expanded ? "minmax(0, 1fr) 260px" : "minmax(0, 1fr) 280px",
        gap: 24,
        maxWidth: expanded ? 1100 : 1280,
        margin: "0 auto",
      }}
    >
      {/* Main column — breadcrumb + action row sit on the page; the hero,
          tabs, and tab content live INSIDE one elevated container card so the
          profile reads as a single cohesive surface (layered above the page),
          not loose sections floating on the canvas. Inside the card the hero
          sections are flush, separated by hairline borders. */}
      <main style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <Breadcrumb segments={segments} />
          <AgentActionRow node={detail} canEdit={canEdit} />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            background: "var(--color-background-primary)",
            border: "0.5px solid var(--color-border-secondary)",
            borderRadius: "var(--border-radius-lg)",
            padding: "4px 20px 20px",
          }}
        >
          <AgentProfileHeader node={detail} canEdit={canEdit} />
          <AgentLiveActivity agentId={detail.id} agentName={detail.name} />
          <AgentDecisionsAndLearnings
            agentId={detail.id}
            agentName={detail.name}
            agentCreatedAt={detail.created_at}
          />
          <AgentRetroSnippet agentId={detail.id} />
          <AgentScorecard agentId={detail.id} />
          <AgentProvenanceFooter node={detail} />
          <AgentProfileTabs />
          <AgentProfileTabContent node={detail} isRoot={isRoot} canEdit={canEdit} />
        </div>
      </main>
      <aside style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        <AgentProfileSidebar node={detail} />
      </aside>
    </div>
  );
}
